import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Loader2 } from 'lucide-react';
import { EmailTemplateList } from './EmailTemplateList';
import { EmailTemplateEditor } from './EmailTemplateEditor';
import { EmailTemplateService } from '../services/emailTemplateService';
import type { EmailTemplate } from '../types/EmailTemplateTypes';

function resolveUserLanguage(i18nLang: string): string {
  const primary = i18nLang.split('-')[0].toLowerCase();
  const map: Record<string, string> = {
    en: 'en-US',
    pt: 'pt-BR',
    es: 'es-ES',
    fr: 'fr-FR',
    de: 'de-DE',
  };
  return map[primary] ?? i18nLang;
}

export const EmailTemplatesSettings = () => {
  const { t, i18n } = useTranslation();
  const userLanguage = resolveUserLanguage(i18n.language);

  const { data: orgTemplates, isLoading } = useQuery({
    queryKey: ['email-templates', 'org'],
    queryFn: () => EmailTemplateService.getOrgTemplates(),
  });

  const [editingTemplate, setEditingTemplate] = useState<EmailTemplate | null>(null);

  const handleEdit = (template: EmailTemplate) => {
    setEditingTemplate(template);
  };

  const slugTemplates = editingTemplate
    ? (orgTemplates ?? []).filter(t => t.slug === editingTemplate.slug)
    : [];

  if (editingTemplate) {
    return (
      <EmailTemplateEditor
        template={editingTemplate}
        slugTemplates={slugTemplates}
        onBack={() => setEditingTemplate(null)}
      />
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('settings.emailTemplates.title')}</CardTitle>
        <CardDescription>{t('settings.emailTemplates.description')}</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <EmailTemplateList
            templates={orgTemplates ?? []}
            userLanguage={userLanguage}
            onEdit={handleEdit}
          />
        )}
      </CardContent>
    </Card>
  );
};
